import DefaultLayout from "@/layouts/DefaultLayout.vue"; // Import DefaultLayout
import EmptyLayout from "@/layouts/EmptyLayout.vue"; // Import EmptyLayout
import HomePage from "../views/HomePage.vue";
import PreviewPage from "../views/PreviewPage.vue";
import ProfileBaseLayout from "../views/ProfileBaseLayout.vue";
import AccountPage from "../views/Profile/AccountPage.vue";
import SecuritySettingsPage from "../views/Profile/SecuritySettingsPage.vue";
import MyResourcesPage from "../views/Profile/MyResourcesPage.vue";
import LessonPackagePage from "../views/LessonPackagePage.vue";
import EditPage from "../views/EditPage.vue";
import PrintPage from "../views/PrintPage.vue";
import LessonLibraryPage from "../views/LessonLibraryPage.vue";
import ViewLessonLibrary from "../views/LessonLibrary/ViewLessonLibrary.vue";
import ProfessionalDevelopmentPage from "../views/ProfessionalDevelopmentPage.vue";
import ChallengeOverviewPage from "../views/RoboticArmChallenges/ChallengeOverviewPage.vue";
import ViewProfessionalDevelopment from "../views/ViewProfessionalDevelopmentPage.vue";
import LessonMaterialsPage from "../views/LessonMaterials/LessonMaterialsPage.vue";
import OnboardingPage from "../views/Onboarding/OnboardingPage.vue";
import TrainerAcademyPage from "../views/TrainerAcademyPage.vue";
import PrivacyPolicyPage from "../views/PrivacyPolicyPage.vue";
import TermsOfServicePage from "../views/TermsOfServicePage.vue";
import { isTrainer } from "./middlewares";

const mainRoutes = [
  {
    path: "/",
    component: DefaultLayout,
    children: [
      {
        path: "",
        name: "HomePage",
        component: HomePage,
        meta: { title: "Home" },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicyPage",
        component: PrivacyPolicyPage,
        meta: { requiresAuth: false, title: "Privacy Policy" },
      },
      {
        path: "terms-of-service",
        name: "TermsOfServicePage",
        component: TermsOfServicePage,
        meta: { requiresAuth: false, title: "Terms of Service" },
      },
      {
        path: "preview",
        name: "PreviewPage",
        component: PreviewPage,
        meta: { page: "preview", title: "Preview" },
      },
      {
        path: "profile",
        component: ProfileBaseLayout,
        meta: { requiresAuth: true },
        children: [
          {
            path: "account",
            name: "AccountPage",
            component: AccountPage,
            meta: { requiresAuth: true, title: "Profile - Account" },
          },
          {
            path: "security-settings",
            name: "SecuritySettingsPage",
            component: SecuritySettingsPage,
            meta: { requiresAuth: true, title: "Profile - Security Settings" },
          },
          {
            path: "my-resources",
            name: "MyResourcesPage",
            component: MyResourcesPage,
            meta: { requiresAuth: true, title: "Profile - My Resources" },
          },
        ],
      },
      {
        path: "my-resources/edtech-studio/:id",
        name: "MyLessonPackagePage",
        component: LessonPackagePage,
        meta: { requiresAuth: true, title: "Lesson Package" },
      },
      {
        path: "my-resources/makercart-activities/:id",
        name: "MyMakerCartActivitiesPage",
        component: LessonMaterialsPage,
        meta: {
          requiresAuth: true,
          title: "Lesson Materials",
        },
      },
      {
        path: "lesson-packages/:id",
        name: "LessonPackagePage",
        component: LessonPackagePage,
        meta: { requiresAuth: true, page: "preview", title: "Lesson Package" },
        props: true,
      },
      {
        path: "edit-packages/:id",
        name: "EditPage",
        component: EditPage,
        meta: { requiresAuth: true, page: "edit", title: "Edit Package" },
      },
      {
        path: "lesson-library",
        name: "LessonLibraryPage",
        component: LessonLibraryPage,
        meta: { title: "Lesson Library" },
      },
      {
        path: "lesson-library/:slug",
        name: "ViewLessonLibrary",
        component: ViewLessonLibrary,
        meta: { title: "Lesson Library", manageTitleManually: true },
      },
      {
        path: "professional-development",
        name: "ProfessionalDevelopmentPage",
        component: ProfessionalDevelopmentPage,
        meta: { title: "Professional Development" },
      },
      {
        path: "professional-development/:slug",
        name: "ViewProfessionalDevelopment",
        component: ViewProfessionalDevelopment,
        meta: { title: "Professional Development", manageTitleManually: true },
      },
      {
        path: "robotic-arm-challenges",
        name: "ChallengeOverviewPage",
        component: ChallengeOverviewPage,
        meta: { title: "Challenge Overview" },
      },
      {
        path: "robotic-arm-challenges/2-1",
        name: "Challenge21Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge21Page.vue"),
        meta: { title: "Challenge 2.1 - Servo Motors" },
      },
      {
        path: "robotic-arm-challenges/2-2",
        name: "Challenge22Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge22Page.vue"),
        meta: { title: "Challenge 2.2 - Servo Motors" },
      },
      {
        path: "robotic-arm-challenges/2-3",
        name: "Challenge23Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge23Page.vue"),
        meta: { title: "Challenge 2.3 - Servo Motors" },
      },
      {
        path: "robotic-arm-challenges/3-1",
        name: "Challenge31Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge31Page.vue"),
        meta: { title: "Challenge 3.1 - Virtual Arm" },
      },
      {
        path: "robotic-arm-challenges/3-2",
        name: "Challenge32Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge32Page.vue"),
        meta: { title: "Challenge 3.2 - Real Arm Digital Twin" },
      },
      {
        path: "robotic-arm-challenges/4-1",
        name: "Challenge41Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge41Page.vue"),
        meta: { title: "Challenge 4.1 - Gesture AI" },
      },
      {
        path: "robotic-arm-challenges/4-2",
        name: "Challenge42Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge42Page.vue"),
        meta: { title: "Challenge 4.2 - Gesture-Controlled Real Arm" },
      },
      {
        path: "robotic-arm-challenges/5",
        name: "Challenge5Page",
        component: () =>
          import("../views/RoboticArmChallenges/Challenge5Page.vue"),
        meta: { title: "Challenge 5 - Autonomous Movement, Part 1" },
      },
      {
        path: "lesson-materials/:id",
        name: "LessonMaterialsPage",
        component: LessonMaterialsPage,
        meta: {
          requiresAuth: false,
          title: "Lesson Materials",
          manageTitleManually: true,
        },
      },
      {
        path: "trainer-academy",
        name: "TrainerAcademyPage",
        beforeEnter: async () => {
          const allowed = await isTrainer();

          if (!allowed) {
            return { path: "/" };
          }
        },
        component: TrainerAcademyPage,
        meta: { requiresAuth: true, title: "Trainer Academy" },
      },
    ],
  },
  {
    path: "/print/:id",
    component: EmptyLayout,
    children: [
      {
        path: "",
        name: "PrintPage",
        component: PrintPage,
        meta: {
          requiresAuth: false,
          header: "hidden",
          footer: "hidden",
          page: "print",
          title: "Print",
        },
      },
    ],
  },
  {
    path: "/onboarding",
    name: "OnboardingPage",
    component: OnboardingPage,
    meta: { title: "Onboarding" },
  },
];

export default mainRoutes;
